import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';

import { LatencyCard, LatencyTooltip } from './LatencyCard';
import { CHART_STYLES } from '../../Dashboard/constants/chartStyles';
import { timeAxisFormat, type LatencyPeriod } from '../const';
import type { TimeseriesResponse } from '../../../../../api';

type Props = {
  data?: TimeseriesResponse | null;
  loading?: boolean;
  period?: LatencyPeriod;
  metricLabel: string;
  unit?: string;
};

export const LatencyTimeseriesCard: React.FC<Props> = ({
  data,
  loading,
  period,
  metricLabel,
  unit = 'ms',
}) => {
  const tickFormat = timeAxisFormat(period);
  const chartData = (data?.points ?? []).map(point => ({
    name: format(new Date(point.timestamp), tickFormat),
    value: point.value ?? 0,
  }));

  return (
    <LatencyCard
      title={`${metricLabel} — p95 over time`}
      subtitle="95th percentile per time bucket"
      loading={loading}
      isEmpty={chartData.length === 0}
      height="h-full min-h-[360px]"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_STYLES.COLORS.GRID}
          />
          <XAxis
            dataKey="name"
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={11}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value: number) => `${Math.round(value)}`}
          />
          <Tooltip
            cursor={{ stroke: CHART_STYLES.COLORS.GRID }}
            isAnimationActive={false}
            content={<LatencyTooltip unit={unit} />}
          />
          <Line
            type="monotone"
            dataKey="value"
            name="p95"
            stroke={CHART_STYLES.COLORS.PRIMARY}
            strokeWidth={2}
            dot={false}
            activeDot={{
              r: 3,
              fill: CHART_STYLES.COLORS.PRIMARY,
              stroke: '#fff',
              strokeWidth: 1,
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </LatencyCard>
  );
};
